import { useCallback, useEffect, useState } from 'react';
import Cookies from 'js-cookie';

export const COOKIE_CONSENT_NAME = 'gameside_cookie_consent';
export const COOKIE_CONSENT_MAX_AGE_DAYS = 180;

export type CookieConsentChoice = 'accepted' | 'refused';

function isCookieConsentChoice(value: string | undefined): value is CookieConsentChoice {
  return value === 'accepted' || value === 'refused';
}

function getCookieOptions() {
  return {
    expires: COOKIE_CONSENT_MAX_AGE_DAYS,
    sameSite: 'Lax' as const,
    secure: typeof window !== 'undefined' && window.location.protocol === 'https:',
  };
}

export function getCookieConsentChoice(): CookieConsentChoice | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const value = Cookies.get(COOKIE_CONSENT_NAME);
  return isCookieConsentChoice(value) ? value : null;
}

export function setCookieConsentChoice(choice: CookieConsentChoice) {
  Cookies.set(COOKIE_CONSENT_NAME, choice, getCookieOptions());
  window.dispatchEvent(new CustomEvent<CookieConsentChoice>('gameside-cookie-consent-change', { detail: choice }));
}

export function resetCookieConsentChoice() {
  Cookies.remove(COOKIE_CONSENT_NAME, { sameSite: 'Lax' });
  window.dispatchEvent(new CustomEvent<null>('gameside-cookie-consent-change', { detail: null }));
}

export function useCookieConsent() {
  const [choice, setChoiceState] = useState<CookieConsentChoice | null>(() => getCookieConsentChoice());

  useEffect(() => {
    function handleChange(event: Event) {
      const nextChoice = (event as CustomEvent<CookieConsentChoice | null>).detail;
      setChoiceState(isCookieConsentChoice(nextChoice || undefined) ? nextChoice : getCookieConsentChoice());
    }

    window.addEventListener('gameside-cookie-consent-change', handleChange);
    return () => window.removeEventListener('gameside-cookie-consent-change', handleChange);
  }, []);

  const setChoice = useCallback((nextChoice: CookieConsentChoice) => {
    setCookieConsentChoice(nextChoice);
  }, []);

  const resetChoice = useCallback(() => {
    resetCookieConsentChoice();
  }, []);

  return {
    choice,
    hasAnswered: choice !== null,
    acceptsOptionalCookies: choice === 'accepted',
    setChoice,
    resetChoice,
  };
}
